import { useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Show, UserButton } from "@clerk/react";
import { Menu, X, Search, ChevronDown, Heart, GitCompareArrows } from "lucide-react";
import { useMe } from "@/lib/api";
import { CAR_TYPES } from "@/lib/brand";
import { useCompare } from "@/lib/collections";
import { Footer } from "@/components/site/Footer";
import { SignInLink } from "@/components/site/PageBits";

const MENUS = [
  { label: "Cars", id: "nav-cars", to: "/cars", items: [{ to: "/cars", label: "All cars", id: "all" }, ...CAR_TYPES.map((t) => ({ to: `/cars?type=${t.type}`, label: t.label, id: t.type }))] },
  { label: "How it Works", id: "nav-how", to: "/#how-it-works" },
  { label: "Host & Earn", id: "nav-host", to: "/host" },
  { label: "Business", id: "nav-business", to: "/business", items: [{ to: "/business", label: "Corporate mobility", id: "corporate" }, { to: "/cars?driver=1", label: "Chauffeur-driven", id: "chauffeur" }, { to: "/offers", label: "Offers & coupons", id: "offers" }] },
  { label: "Support", id: "nav-support", to: "/support", items: [{ to: "/support", label: "Help & tickets", id: "tickets" }, { to: "/faq", label: "FAQ", id: "faq" }, { to: "/contact", label: "Contact us", id: "contact" }] },
];

export const Logo = ({ dark }) => (
  <Link to="/" data-testid="brand-logo" className="group flex items-center gap-2.5">
    <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-hc-green shadow-[0_6px_16px_-6px_rgba(15,81,50,.6)] ring-1 ring-hc-gold/40 transition-transform duration-300 group-hover:-rotate-6">
      <img src="/mark-gold.png" alt="" className="h-7 w-7" />
    </span>
    <span className={`font-display text-[22px] font-extrabold italic tracking-tight ${dark ? "text-white" : "text-hc-ink"}`}>
      Hoore<span className="text-hc-gold">Cars</span>
    </span>
  </Link>
);

const linkCls = "flex items-center gap-1 text-[15px] font-medium text-hc-ink/85 transition-colors duration-200 hover:text-hc-green";

function NavItem({ m }) {
  const scroll = () => m.to.includes("#") && setTimeout(() => document.getElementById(m.to.split("#")[1])?.scrollIntoView({ behavior: "smooth" }), 30);
  if (!m.items) return <Link to={m.to} onClick={scroll} className={linkCls} data-testid={m.id}>{m.label}</Link>;
  return (
    <div className="group relative" data-testid={`${m.id}-menu`}>
      <Link to={m.to} className={linkCls} data-testid={m.id}>{m.label}<ChevronDown className="h-4 w-4 transition-transform duration-200 group-hover:rotate-180" /></Link>
      <div className="invisible absolute left-1/2 top-full z-50 w-60 -translate-x-1/2 pt-3 opacity-0 transition-[opacity,visibility] duration-200 group-hover:visible group-hover:opacity-100">
        <div className="rounded-2xl border border-hc-sage bg-white p-2 shadow-xl">
          {m.items.map((it) => (
            <Link key={it.to} to={it.to} data-testid={`${m.id}-${it.id}`} className="block rounded-xl px-3 py-2 text-sm text-hc-ink/80 transition-colors duration-150 hover:bg-hc-mint hover:text-hc-green">{it.label}</Link>
          ))}
        </div>
      </div>
    </div>
  );
}

function AuthButtons({ me }) {
  return (
    <>
      <Show when="signed-out">
        <SignInLink testId="nav-signin-btn" className="hidden rounded-xl border border-hc-sage bg-white px-5 py-2.5 text-sm font-semibold text-hc-ink transition-colors duration-200 hover:border-hc-green hover:text-hc-green sm:block">Sign in</SignInLink>
        <SignInLink testId="nav-get-started-btn" className="btn-primary !px-5 !py-2.5">Get Started</SignInLink>
      </Show>
      <Show when="signed-in">
        <Link to="/wishlist" aria-label="Wishlist" data-testid="nav-wishlist" className="hidden rounded-full p-2 text-hc-ink transition-colors duration-200 hover:bg-hc-mint hover:text-hc-green sm:block"><Heart className="h-5 w-5" /></Link>
        {me?.is_host && <Link to="/host/dashboard" data-testid="nav-host-dashboard" className="btn-outline hidden xl:inline-flex">Host dashboard</Link>}
        <Link to="/bookings" data-testid="nav-bookings" className="btn-outline hidden sm:inline-flex">My bookings</Link>
        <div data-testid="nav-user-button"><UserButton /></div>
      </Show>
    </>
  );
}

function MobileMenu({ me, close }) {
  const cls = "text-[15px] font-medium text-hc-ink";
  const items = [["/cars", "All cars"], ["/#how-it-works", "How it works"], ["/host", "Host & Earn"], ["/business", "Business"], ["/offers", "Offers"], ["/bookings", "My bookings"], ["/wishlist", "Wishlist"], ["/verify", "Driver verification"], ["/support", "Help & support"]];
  return (
    <div className="border-t border-hc-sage bg-white lg:hidden" data-testid="nav-mobile-menu">
      <div className="container-x grid grid-cols-2 gap-4 py-5">
        {items.map(([to, l]) => <Link key={to} to={to} onClick={close} className={cls} data-testid={`m-nav-${l.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{l}</Link>)}
        {me?.is_host && <NavLink to="/host/dashboard" onClick={close} className={cls} data-testid="m-nav-host-dashboard">Host dashboard</NavLink>}
        {me?.role === "admin" && <NavLink to="/admin" onClick={close} className={cls} data-testid="m-nav-admin">Admin</NavLink>}
        <Show when="signed-out"><SignInLink className="btn-outline w-fit" testId="m-nav-signin">Sign in</SignInLink></Show>
      </div>
    </div>
  );
}

function Navbar() {
  const [open, setOpen] = useState(false);
  const { me } = useMe();
  return (
    <header className="sticky top-0 z-40 border-b border-hc-sage/80 bg-white/90 backdrop-blur-xl">
      <div className="container-x flex h-[72px] items-center justify-between gap-4">
        <Logo />
        <nav className="hidden items-center gap-7 lg:flex">
          {MENUS.map((m) => <NavItem key={m.id} m={m} />)}
          {me?.role === "admin" && <NavLink to="/admin" className={linkCls} data-testid="nav-admin">Admin</NavLink>}
        </nav>
        <div className="flex items-center gap-2 sm:gap-3">
          <Link to="/cars" aria-label="Search cars" data-testid="nav-search-btn" className="hidden rounded-full p-2 text-hc-ink transition-colors duration-200 hover:bg-hc-mint hover:text-hc-green md:block"><Search className="h-5 w-5" /></Link>
          <AuthButtons me={me} />
          <button className="rounded-lg p-1.5 lg:hidden" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle" aria-label="Menu">
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>
      {open && <MobileMenu me={me} close={() => setOpen(false)} />}
    </header>
  );
}

function CompareTray() {
  const { ids, clear } = useCompare();
  const { pathname } = useLocation();
  if (ids.length === 0 || pathname === "/compare" || pathname.startsWith("/checkout")) return null;
  return (
    <div className="fixed bottom-4 left-1/2 z-40 flex -translate-x-1/2 items-center gap-3 rounded-2xl bg-hc-ink px-4 py-3 text-white shadow-2xl fade-up" data-testid="compare-tray">
      <GitCompareArrows className="h-5 w-5 text-hc-gold" />
      <span className="text-sm font-medium" data-testid="compare-tray-count">{ids.length} car{ids.length > 1 ? "s" : ""} selected</span>
      <Link to={`/compare?ids=${ids.join(",")}`} className="rounded-lg bg-hc-gold px-4 py-1.5 text-sm font-semibold text-hc-ink transition-colors duration-200 hover:bg-hc-goldh" data-testid="compare-tray-open">Compare</Link>
      <button onClick={clear} className="text-xs text-white/70 hover:text-white" data-testid="compare-tray-clear">Clear</button>
    </div>
  );
}

export default function SiteLayout() {
  return (
    <div className="flex min-h-screen flex-col bg-hc-cream">
      <Navbar />
      <main className="flex-1"><Outlet /></main>
      <CompareTray />
      <Footer />
    </div>
  );
}
